<template>
  <article class="chat-message">
    <header class="chat-message__header">
      <span class="chat-message__avatar">{{ authorInitial }}</span>
      <div>
        <strong>{{ authorName }}</strong>
        <time v-if="formattedDate">{{ formattedDate }}</time>
      </div>
      <button
        v-if="message.isCurrentUser"
        type="button"
        class="chat-message__delete"
        aria-label="Видалити повідомлення"
        @click="removeMessage(message.id)"
      >
        <v-icon size="17">mdi-delete-outline</v-icon>
      </button>
    </header>

    <div
      v-if="message.text"
      class="chat-message__text"
      :class="{ 'chat-message__text--exercise': isExerciseMessage }"
      v-html="message.text"
    ></div>

    <div
      v-if="message.files && message.files.length"
      class="chat-message__files"
    >
      <a
        v-for="fileItem in message.files"
        :key="fileItem.id"
        :href="fileItem.file"
        target="_blank"
        rel="noopener noreferrer"
        :download="fileItem.name"
      >
        <v-icon color="inherit" size="19">mdi-paperclip</v-icon>
        <span>{{ fileItem.name }}</span>
        <v-icon color="inherit" size="18">mdi-download-outline</v-icon>
      </a>
    </div>

    <footer v-if="message.isCurrentUser" class="chat-message__status">
      <v-icon :color="message.isRead ? '#24795d' : '#98a2b3'" size="16">
        {{ message.isRead ? 'mdi-check-all' : 'mdi-check' }}
      </v-icon>
      <span>{{ message.isRead ? 'Прочитано' : 'Надіслано' }}</span>
    </footer>
  </article>
</template>

<script>
import messageEventEnum from '@/enums/messageEventEnum';

export default {
  name: 'Message',
  props: {
    message: {
      type: Object,
      required: false,
      default: function () {
        return {};
      },
    },
  },
  data() {
    return {};
  },
  computed: {
    authorName() {
      const user = this.message.userView || {};
      return (
        [user.firstName, user.lastName].filter(Boolean).join(' ') ||
        'Користувач'
      );
    },
    authorInitial() {
      return this.authorName.charAt(0).toUpperCase();
    },
    formattedDate() {
      if (!this.message.date) return '';
      return new Intl.DateTimeFormat('uk-UA', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(this.message.date));
    },
    isExerciseMessage() {
      const container = document.createElement('div');
      container.innerHTML = this.message.text || '';
      const text = container.textContent || container.innerText || '';
      return /(?:^|\s)Вправа\s+\d+/iu.test(text.trim());
    },
  },
  mounted() {},
  methods: {
    removeMessage(id) {
      this.$emit('messageEvent', {
        type: messageEventEnum.REMOVE_MESSAGE,
        data: {
          id,
        },
      });
    },
  },
};
</script>

<style lang="less" scoped>
.chat-message {
  min-width: 0;
}
.chat-message__header {
  align-items: center;
  display: flex;
  gap: 9px;
  margin-bottom: 8px;
}
.chat-message__avatar {
  align-items: center;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 9px;
  color: #24795d;
  display: flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 900;
  height: 30px;
  justify-content: center;
  width: 30px;
}
.chat-message__header strong,
.chat-message__header time {
  display: block;
}
.chat-message__header strong {
  color: #344054;
  font-size: 13px;
  line-height: 1.2;
}
.chat-message__header time {
  color: #7a8592;
  font-size: 10px;
  margin-top: 2px;
}
.chat-message__delete {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: #7a8592;
  cursor: pointer;
  display: flex;
  height: 28px;
  justify-content: center;
  margin-left: auto;
  opacity: 0.35;
  width: 28px;
}
.chat-message:hover .chat-message__delete,
.chat-message__delete:focus {
  opacity: 1;
}
.chat-message__text {
  color: #344054;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.chat-message__text::v-deep p {
  margin: 0 0 7px;
}
.chat-message__text::v-deep p:last-child {
  margin-bottom: 0;
}
.chat-message__text--exercise::v-deep h1,
.chat-message__text--exercise::v-deep h2,
.chat-message__text--exercise::v-deep h3 {
  background: var(--theme-surface-decorative);
  border-left: 3px solid var(--theme-header-background);
  border-radius: 0 9px 9px 0;
  color: var(--theme-banner-text);
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 1.45;
  margin: 0 0 6px;
  padding: 9px 12px;
}
.chat-message__text--exercise::v-deep h1:first-child::before,
.chat-message__text--exercise::v-deep h2:first-child::before,
.chat-message__text--exercise::v-deep h3:first-child::before {
  color: var(--theme-header-background);
  content: 'Завдання';
  display: block;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  letter-spacing: 0.06em;
  margin-bottom: 3px;
  text-transform: uppercase;
}
.chat-message__text--exercise::v-deep h1 em,
.chat-message__text--exercise::v-deep h2 em,
.chat-message__text--exercise::v-deep h3 em {
  font-style: normal;
}
.chat-message__text--exercise::v-deep h1 + p,
.chat-message__text--exercise::v-deep h2 + p,
.chat-message__text--exercise::v-deep h3 + p {
  margin-top: 12px;
}
.chat-message__files {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 9px;
}
.chat-message__files a {
  align-items: center;
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid rgba(36, 121, 93, 0.17);
  border-radius: 9px;
  color: #24795d;
  display: flex;
  font-size: 12px;
  gap: 7px;
  max-width: 100%;
  padding: 7px 9px;
  text-decoration: none;
}
.chat-message__files a span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat-message__status {
  align-items: center;
  color: #7a8592;
  display: flex;
  font-size: 10px;
  gap: 3px;
  justify-content: flex-end;
  margin-top: 6px;
}
</style>
